import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Store, ShieldCheck, GraduationCap, Tag, Wallet, LogIn } from "lucide-react";
import { api, CATEGORY_IMAGES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const BENTO_SPANS = [
  "md:col-span-4 h-72",
  "md:col-span-2 h-72",
  "md:col-span-2 h-56",
  "md:col-span-2 h-56",
  "md:col-span-2 h-56",
  "md:col-span-4 h-60",
];

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [specials, setSpecials] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories)).catch(() => {});
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.products)).catch(() => {});
    api.get("/products", { params: { special: true, limit: 4 } }).then((r) => setSpecials(r.data.products)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo />
      <Hero />
      <Marquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20" data-testid="category-bento">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand mb-3">Departments</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
            Shop by what you need
          </h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-6 gap-4">
          {categories.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.06} className={BENTO_SPANS[i % BENTO_SPANS.length]}>
              <Link
                to={`/shop/${c.slug}`}
                className="group relative block w-full h-full rounded-3xl overflow-hidden border border-slate-200/70"
                data-testid={`category-tile-${c.slug}`}
              >
                <img
                  src={c.image || CATEGORY_IMAGES[c.slug]}
                  alt={c.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/85 via-navy-dark/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="font-display font-bold text-white text-xl">{c.name}</h3>
                  <p className="text-white/70 text-sm mt-0.5">
                    {c.tagline} · {c.count} products
                  </p>
                </div>
                <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight size={16} />
                </span>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.36} className="md:col-span-2 h-60">
            <Link
              to="/schools"
              className="group relative block w-full h-full rounded-3xl overflow-hidden border border-slate-200/70"
              data-testid="category-tile-assorted-schools"
            >
              <img
                src="/api/files/btmedu/uploads/902c41cd-3004-40b1-9953-feb50f194c63/610fbc08706d40699dd3de585819c831.jpg"
                alt="Assorted Schools"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/85 via-navy-dark/20 to-transparent" />
              <span className="absolute top-5 right-5 w-11 h-11 rounded-2xl bg-brand flex items-center justify-center text-white">
                <GraduationCap size={20} />
              </span>
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display font-bold text-white text-xl">Assorted Schools</h3>
                <p className="text-white/80 text-sm mt-0.5">Prescribed lists by grade</p>
              </div>
              <span className="absolute bottom-6 right-5 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight size={16} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid="featured-section">
        <Reveal className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand mb-3">Shop our collection</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">
              Useful picks for classrooms & study sessions
            </h2>
          </div>
          <Link
            to="/shop"
            className="text-sm font-bold text-navy hover:text-brand flex items-center gap-1.5 transition-colors"
            data-testid="view-all-products-link"
          >
            View all products <ArrowRight size={15} />
          </Link>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {specials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid="specials-teaser">
          <Reveal>
            <div className="relative bg-cream rounded-3xl border border-gold/30 p-8 sm:p-10 overflow-hidden">
              <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-gold/15 blur-3xl" />
              <div className="relative flex flex-wrap items-center justify-between gap-6">
                <div>
                  <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-gold-hover mb-3">
                    <Tag size={13} /> Specials worth a look
                  </p>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-navy">
                    Save on school essentials
                  </h2>
                  <p className="text-sm text-slate-600 mt-2">
                    {specials.length} selected offer{specials.length !== 1 ? "s" : ""} live right now.
                  </p>
                </div>
                <Link
                  to="/specials"
                  className="px-7 py-3.5 rounded-full bg-navy hover:bg-navy-light text-white font-bold text-sm flex items-center gap-2 transition-colors"
                  data-testid="shop-specials-cta"
                >
                  Shop specials <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid="schools-teaser">
        <Reveal>
          <div className="relative bg-navy rounded-3xl overflow-hidden text-white p-8 sm:p-12">
            <div className="absolute inset-0 hero-grid-lines opacity-60" />
            <div className="absolute -left-24 -bottom-24 w-72 h-72 rounded-full bg-brand/25 blur-[100px]" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-gold mb-4">
                  <GraduationCap size={14} /> Assorted Schools
                </p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                  Your school's prescribed list, in one cart.
                </h2>
                <p className="text-white/70 text-sm sm:text-base mt-4 max-w-md leading-relaxed">
                  Pick your school, pick the grade, and tick off the exact stationery and textbooks your
                  teachers prescribed. No guesswork, no forgotten items.
                </p>
                <Link
                  to="/schools"
                  className="inline-flex mt-7 px-7 py-3.5 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm items-center gap-2 transition-colors gold-glow"
                  data-testid="schools-teaser-cta"
                >
                  Find your school <ArrowRight size={15} />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {["Choose school", "Select grade", "Tick & add to cart"].map((s, i) => (
                  <div key={s} className="bg-white/10 border border-white/15 rounded-2xl p-4 backdrop-blur">
                    <p className="font-display font-extrabold text-2xl text-gold">{i + 1}</p>
                    <p className="text-xs font-semibold mt-2 leading-snug">{s}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid="stokvel-teaser">
        <Reveal>
          <div className="relative bg-cream rounded-3xl border border-gold/40 p-8 sm:p-12 overflow-hidden">
            <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-gold/20 blur-[90px]" />
            <div className="absolute -left-16 -top-16 w-56 h-56 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-8">
              <div className="max-w-xl">
                <p className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-gold-hover mb-4">
                  <Wallet size={14} /> Members save together
                </p>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-navy" data-testid="stokvel-home-heading">
                  BTMEDU STATIONERY STOKVEL
                </h2>
                <p className="font-display text-base sm:text-lg font-bold text-brand mt-2" data-testid="stokvel-home-tagline">
                  Save Together. Shop Smarter. Get Your Stationery with BTMEDU.
                </p>
                <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed" data-testid="stokvel-home-description">
                  Join the BTMEDU Stationery Stokvel, contribute towards your stationery purchases and use your accumulated balance to shop exclusively at BTMEDU.
                </p>
              </div>
              <div className="flex flex-col gap-3 shrink-0">
                {user ? (
                  <Link
                    to="/stokvel"
                    className="px-7 py-3.5 rounded-full bg-navy hover:bg-navy-light text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                    data-testid="stokvel-home-wallet-cta"
                  >
                    Open my Stokvel wallet <ArrowRight size={15} />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/auth?mode=register&next=/stokvel"
                      className="px-7 py-3.5 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors gold-glow"
                      data-testid="stokvel-home-register-cta"
                    >
                      Register Now <ArrowRight size={15} />
                    </Link>
                    <Link
                      to="/auth?next=/stokvel"
                      className="px-7 py-3.5 rounded-full bg-navy hover:bg-navy-light text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                      data-testid="stokvel-home-login-cta"
                    >
                      <LogIn size={15} /> Login
                    </Link>
                  </>
                )}
                <Link to="/stokvel" className="text-center text-xs font-bold text-navy hover:text-brand transition-colors" data-testid="stokvel-home-learn">
                  How the Stokvel works
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="stokvel-about">
        <Reveal>
          <div className="bg-white rounded-3xl border border-slate-200/80 p-8 sm:p-10 max-w-4xl mx-auto">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-navy" data-testid="stokvel-about-heading">
              What is the Stationery Stokvel?
            </h2>
            <p className="text-sm sm:text-base font-bold text-navy mt-4 leading-relaxed" data-testid="stokvel-about-hook">
              If You Can Save for Alcohol, vacations, get togethers and Groceries, Surely You Can Save for Your Children's Future!
              We find money for the things we want today. But what about the things our children need tomorrow?
              Your child's education should never be an afterthought. Don't wait for January to struggle with stationery expenses. Start saving today!
            </p>
            <p className="text-sm sm:text-base text-slate-600 mt-4 leading-relaxed" data-testid="stokvel-about-body">
              The BTMEDU Stationery Stokvel helps parents and customers plan ahead for stationery expenses. Save for as long as you want, contribute monthly or whenever you can, and watch your savings grow. When you're ready, use your accumulated Stokvel balance to purchase stationery and eligible products exclusively from BTMEDU.
            </p>
            <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">
              You can save throughout the year and use your savings at the end of the year for the new school year, or shop whenever you have accumulated enough.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid="trust-cards">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: Truck, title: "Delivery that works for you", body: "Choose delivery at checkout. Fees and service areas confirmed before payment — free over R750." },
            { icon: Store, title: "Collect from the shop", body: "Prefer to collect? Select collection and wait for your order-ready confirmation before pickup." },
            { icon: ShieldCheck, title: "Checkout with confidence", body: "Secure payment gateway (PayFast-ready). No card details are ever stored on BTM servers." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 h-full hover:border-brand/30 transition-colors" data-testid={`trust-card-${i}`}>
                <span className="w-11 h-11 rounded-2xl bg-cream flex items-center justify-center text-brand">
                  <c.icon size={20} />
                </span>
                <h3 className="font-display font-bold text-navy mt-4">{c.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20" data-testid="bulk-quote-band">
        <Reveal>
          <div className="bg-white rounded-3xl border border-slate-200/80 p-8 sm:p-10 flex flex-wrap items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-navy">
                Buying for a school, class or business?
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                Send us your stationery list or request a bulk quotation.
              </p>
            </div>
            <Link
              to="/quote"
              className="px-7 py-3.5 rounded-full bg-brand hover:bg-brand-accent text-white font-bold text-sm flex items-center gap-2 transition-colors"
              data-testid="request-quote-cta"
            >
              Request a quotation <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
